import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ReportAssault() {
    const [form, setForm] = useState({ incident_type: "verbal", location: "", description: "", occurred_at: "", anonymous: false });
    const [done, setDone] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.location || !form.description || !form.occurred_at) { toast.error("Please complete all fields"); return; }
        try { await api.post("/incidents", form); setDone(true); toast.success("Report submitted securely"); }
        catch { toast.error("Could not submit report"); }
    };

    if (done) return (
        <div className="max-w-lg mx-auto text-center py-16" data-testid="incident-success">
            <div className="h-16 w-16 rounded-full bg-nhs-teal/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-nhs-teal" /></div>
            <h1 className="font-heading text-2xl font-bold text-nhs-ink mt-6">Report submitted</h1>
            <p className="text-slate-500 mt-2">Your report has been securely recorded and routed to the safeguarding team. You are not alone — support is available.</p>
            <Button onClick={() => { setDone(false); setForm({ incident_type: "verbal", location: "", description: "", occurred_at: "", anonymous: false }); }} className="mt-6 bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="report-another">Submit another report</Button>
        </div>
    );

    return (
        <div className="max-w-2xl" data-testid="report-page">
            <div className="flex items-center gap-3 mb-2">
                <div className="h-11 w-11 rounded-lg bg-nhs-red/10 flex items-center justify-center"><ShieldAlert className="h-5 w-5 text-nhs-red" /></div>
                <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Report Assault</h1>
            </div>
            <p className="text-slate-500 mb-6">Report a verbal or physical assault. If you are in immediate danger, call security or 999 first.</p>

            <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-5">
                <div>
                    <Label>Type of assault</Label>
                    <Select value={form.incident_type} onValueChange={(v) => set("incident_type", v)}>
                        <SelectTrigger className="mt-1.5" data-testid="incident-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="verbal">Verbal abuse / threats</SelectItem>
                            <SelectItem value="physical">Physical assault</SelectItem>
                            <SelectItem value="racial">Racial abuse</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Ward / department" className="mt-1.5" data-testid="incident-location-input" /></div>
                    <div><Label>When did it happen?</Label><Input type="datetime-local" value={form.occurred_at} onChange={(e) => set("occurred_at", e.target.value)} className="mt-1.5" data-testid="incident-date-input" /></div>
                </div>
                <div><Label>What happened?</Label><Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the incident…" className="mt-1.5" data-testid="incident-description-input" /></div>
                <div className="flex items-center gap-2">
                    <Checkbox id="anon" checked={form.anonymous} onCheckedChange={(v) => set("anonymous", !!v)} data-testid="incident-anonymous-checkbox" />
                    <Label htmlFor="anon" className="text-sm font-normal cursor-pointer">Submit anonymously (your identity will not be recorded)</Label>
                </div>
                <Button type="submit" className="bg-nhs-red hover:bg-[#B51E14] text-white w-full" data-testid="submit-incident-button">Submit report securely</Button>
            </form>
        </div>
    );
}
